/**
 * User-Agent から friendly name を派生する (Track E-3 client-side parsing)。
 *
 * バックエンドは UA を raw で保存し、client 側で短い文字列に整形する。
 * 完璧な UA パースは難しいので、よく見かける ブラウザ × OS の組み合わせのみ識別し、
 * それ以外は元の UA を 50 文字で truncate して表示する。
 *
 * 既知制限: Linux / iOS Safari の判別、Edge と Chrome の混同等は割愛 (Phase 6 で
 * `ua-parser-js` の導入を検討、約 30 KB gzip)。
 */

export interface ParsedUserAgent {
  /** 表示用の friendly name (例: "Chrome / macOS")。null = UA 不明 */
  display: string | null
}

const BROWSER_PATTERNS: ReadonlyArray<{ re: RegExp; name: string }> = [
  { re: /Edg\//u, name: 'Edge' },
  { re: /OPR\//u, name: 'Opera' },
  { re: /Firefox\//u, name: 'Firefox' },
  { re: /Chrome\//u, name: 'Chrome' },
  { re: /Safari\//u, name: 'Safari' },
]

// 順序が重要: iOS は UA に "Mac OS X" を含むため macOS より先にチェック。
// Android は UA に "Linux" を含むため Linux より先にチェック。
const OS_PATTERNS: ReadonlyArray<{ re: RegExp; name: string }> = [
  { re: /Windows NT/u, name: 'Windows' },
  { re: /iPhone|iPad|iPod/u, name: 'iOS' },
  { re: /Android/u, name: 'Android' },
  { re: /Macintosh|Mac OS X/u, name: 'macOS' },
  { re: /Linux/u, name: 'Linux' },
]

export const parseUserAgent = (ua: string | null): ParsedUserAgent => {
  if (ua === null || ua.trim() === '') return { display: null }

  let browser: string | null = null
  for (const p of BROWSER_PATTERNS) {
    if (p.re.test(ua)) {
      browser = p.name
      break
    }
  }

  let os: string | null = null
  for (const p of OS_PATTERNS) {
    if (p.re.test(ua)) {
      os = p.name
      break
    }
  }

  if (browser !== null && os !== null) return { display: `${browser} / ${os}` }
  if (browser !== null) return { display: browser }
  if (os !== null) return { display: os }

  // どちらも識別できない場合は raw を truncate
  return { display: ua.length > 50 ? `${ua.slice(0, 47)}…` : ua }
}
